/**
 * Q4 — N3 · Competência 6 (Estatística) / 2 (Sequências)
 *
 *   "Num gráfico de colunas que segue um padrão, escolher com um seletor um valor
 *    adequado para a coluna que falta."
 *
 * ADAPTADO DA QUESTÃO 04 do material no GeoGebra: vendas do primeiro semestre, em
 * milhões de reais, com o seletor definindo abril de modo que haja crescimento mensal.
 *
 * O ALUNO RESPONDE MEXENDO NA FIGURA. O valor enviado é o do seletor — não há campo
 * para digitar —, e por isso o item é N3: a manipulação É a resposta. Enquanto o
 * seletor não sai do lugar, a coluna aparece tracejada, com "?", e não há resposta.
 *
 * A HABILIDADE FOI DESDOBRADA EM DOIS TEMPLATES, porque "seguir um padrão" esconde duas
 * exigências diferentes:
 *
 *  - TENDÊNCIA (H3.30.K01): "as vendas cresceram todos os meses". Há uma FAIXA de
 *    respostas certas — qualquer valor entre os dois vizinhos —, e o que se mede é a
 *    leitura do gráfico e da tendência. É a versão do GeoGebra.
 *  - AUMENTO CONSTANTE (H1.12.K01): "cresceram sempre a mesma quantia". A resposta é
 *    única, e o que se mede é reconhecer a progressão aritmética e usar a razão.
 *
 * Os valores conhecidos caem sempre numa linha da grade (múltiplos de 5, com números a
 * cada 10): a leitura do gráfico precisa ser exata, senão o item passaria a medir
 * pontaria em vez de padrão.
 */

import { formatBR } from '../../core/num.ts';
import type { Rng } from '../../core/rng.ts';
import { sampleWhere } from '../../core/sampling.ts';
import { barras } from '../../render/chart.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import type { ErrorCode } from '../../schema/errors.ts';
import {
  GRAF_IGUAL_VIZINHO,
  GRAF_PA_DIFERENCA_DOBRADA,
  GRAF_PA_DIFERENCA_ERRADA,
  GRAF_PA_MEDIA,
  GRAF_QUEBRA_TENDENCIA,
} from '../../schema/errors.ts';
import type { ItemTemplate, NumericEntryInteraction } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { comoEm, livre, type Sintagma } from '../../core/pt.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'] as const;
const MESES_NOME = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho'] as const;

type Modo = 'tendencia' | 'progressao';
type Sentido = 'cresce' | 'decresce';

interface Contexto {
  readonly abertura: string;
  readonly titulo: string;
  readonly eixo: string;
  /** Unidade por extenso, depois do número: "40 milhões de reais". */
  readonly unidade: string;
  /**
   * O sujeito da frase, com o seu número declarado — "as vendas" é plural, "a produção"
   * é singular, e é isso que decide a forma de todo verbo que vier depois.
   *
   * Antes eram três campos: o sujeito como texto e duas formas verbais escritas à mão
   * ("cresceram"/"cresceu"). O terceiro verbo que a resolução precisou não tinha campo,
   * e saiu no singular para todo mundo: "as vendas não mudaria". Com o número declarado
   * uma vez, `.vb()` conjuga qualquer verbo do léxico e não há a quarta forma para
   * esquecer.
   */
  readonly sujeito: Sintagma;
  readonly sentidos: readonly Sentido[];
}

/**
 * Contextos de números que o aluno reconhece. O reservatório só aparece diminuindo:
 * "a água do reservatório aumentou todos os meses de uma estiagem" não faria sentido,
 * e contexto incoerente é ruído que o item mede junto.
 */
const CONTEXTOS: readonly Contexto[] = [
  {
    abertura:
      'O gráfico mostra as vendas mensais de uma loja no primeiro semestre do ano, em milhões de reais.',
    titulo: 'Vendas no primeiro semestre',
    eixo: 'Vendas (milhões de R$)',
    unidade: 'milhões de reais',
    sujeito: livre('as vendas', 'f', 'pl'),
    sentidos: ['cresce', 'decresce'],
  },
  {
    abertura:
      'O gráfico mostra quantas pessoas visitaram um museu em cada mês do primeiro semestre, em milhares.',
    titulo: 'Visitantes do museu',
    eixo: 'Visitantes (mil)',
    unidade: 'mil visitantes',
    sujeito: livre('o número de visitantes', 'm', 'sg'),
    sentidos: ['cresce', 'decresce'],
  },
  {
    abertura:
      'Durante uma estiagem, a companhia de água registrou o volume de um reservatório no fim de cada mês, em milhões de litros.',
    titulo: 'Água no reservatório',
    eixo: 'Volume (milhões de litros)',
    unidade: 'milhões de litros',
    sujeito: livre('o volume de água', 'm', 'sg'),
    sentidos: ['decresce'],
  },
  {
    abertura:
      'O gráfico mostra a produção mensal de uma fábrica de bicicletas no primeiro semestre, em milhares de peças.',
    titulo: 'Produção da fábrica',
    eixo: 'Produção (mil peças)',
    unidade: 'mil peças',
    sujeito: livre('a produção', 'f', 'sg'),
    sentidos: ['cresce', 'decresce'],
  },
];

/** Números a cada 10, linhas a cada 5: os valores conhecidos são múltiplos de 5. */
const Y_STEP = 10;
const GRID = 5;

export interface Params {
  readonly modo: Modo;
  readonly sentido: Sentido;
  readonly contexto: number;
  /** Os seis meses; o do alvo, na progressão, é a resposta; na tendência, é descartado. */
  readonly valores: readonly number[];
  readonly alvo: number;
  readonly yTop: number;
  /** Razão da progressão (com sinal); 0 na tendência. */
  readonly razao: number;
}

const topoPara = (max: number): number =>
  Math.min(100, Math.max(60, Math.ceil((max + GRID) / 20) * 20));

function sortearTendencia(
  g: Rng,
  sentido: Sentido,
): Omit<Params, 'modo' | 'sentido' | 'contexto'> | null {
  // Seis múltiplos de 5, distintos e ordenados, entre 5 e 95.
  const pool = Array.from({ length: 19 }, (_v, i) => (i + 1) * GRID);
  const escolhidos = g
    .shuffle(pool)
    .slice(0, 6)
    .sort((a, b) => a - b);
  const valores = sentido === 'cresce' ? escolhidos : [...escolhidos].reverse();
  const alvo = g.int(1, 4);
  const antes = valores[alvo - 1] as number;
  const depois = valores[alvo + 1] as number;
  // A faixa de respostas certas tem pelo menos 9 valores: nem apertada demais para o
  // seletor, nem tão larga que qualquer movimento acerte.
  if (Math.abs(depois - antes) < 2 * GRID || Math.abs(depois - antes) > 40) return null;
  return { valores, alvo, yTop: topoPara(Math.max(...valores)), razao: 0 };
}

function sortearProgressao(
  g: Rng,
  sentido: Sentido,
): Omit<Params, 'modo' | 'sentido' | 'contexto'> | null {
  const r = g.pick([5, 10, 15]);
  const a1 = GRID * g.int(1, 8);
  const serie = Array.from({ length: 6 }, (_v, i) => a1 + i * r);
  if ((serie[5] as number) > 95) return null;
  const valores = sentido === 'cresce' ? serie : [...serie].reverse();
  // O alvo pode ser o último mês: aí não há vizinho depois, e o aluno precisa continuar
  // a sequência em vez de encaixar um valor entre dois.
  const alvo = g.int(1, 5);
  return {
    valores,
    alvo,
    yTop: topoPara(Math.max(...valores)),
    razao: sentido === 'cresce' ? r : -r,
  };
}

function paramsPara(modo: Modo) {
  return (rng: Rng): Params => {
    const contexto = rng.int(0, CONTEXTOS.length - 1);
    const ctx = CONTEXTOS[contexto] as Contexto;
    const sentido = rng.pick(ctx.sentidos);
    const geo = sampleWhere(
      rng.fork('valores'),
      (g) => (modo === 'tendencia' ? sortearTendencia(g, sentido) : sortearProgressao(g, sentido)),
      (c) => c !== null,
      { label: 'valores', maxTries: 200 },
    ) as Omit<Params, 'modo' | 'sentido' | 'contexto'>;
    return { modo, sentido, contexto, ...geo };
  };
}

// ---------------------------------------------------------------------------
// Resposta: faixa, chave e os diagnósticos
// ---------------------------------------------------------------------------

const n0 = (v: number): string => formatBR(v, 0);

interface Resposta {
  readonly chave: number;
  readonly faixa: { readonly lo: number; readonly hi: number } | null;
  readonly erros: NumericEntryInteraction['errorValues'];
  readonly faixasDeErro: NonNullable<NumericEntryInteraction['errorIntervals']>;
}

/** "cresceram"/"cresceu", "diminuíram"/"diminuiu" — concordando com o sujeito do contexto. */
const sentidoDo = (ctx: Contexto, subindo: boolean): string =>
  comoEm(ctx.sujeito, subindo ? 'crescer.pret' : 'diminuir.pret');

function resposta(p: Params): Resposta {
  const ctx = CONTEXTOS[p.contexto] as Contexto;
  const u = ctx.unidade;
  const mes = MESES_NOME[p.alvo] as string;
  const mesAntes = MESES_NOME[p.alvo - 1] as string;
  const mesDepois = MESES_NOME[p.alvo + 1];
  const antes = p.valores[p.alvo - 1] as number;
  const depois = p.alvo + 1 < p.valores.length ? (p.valores[p.alvo + 1] as number) : null;
  const sobe = p.sentido === 'cresce';
  const verboDoSentido = sentidoDo(ctx, sobe);

  const erros: { value: number; error: ErrorCode; explanation: RichText }[] = [];
  const faixasDeErro: { lo: number; hi: number; error: ErrorCode; explanation: RichText }[] = [];

  /**
   * O ALUNO REPETIU O VALOR DO VIZINHO — e o que se diz a ele depende do item.
   *
   * Este arquivo gera DOIS itens, e eles pedem coisas diferentes: na tendência serve
   * qualquer valor entre os vizinhos, na progressão serve um valor só, o que mantém a
   * mesma diferença. A explicação era montada aqui, antes do desvio de modo, e dava a
   * regra da tendência aos dois — quem errava a progressão aprendia o critério do item
   * irmão, mais frouxo que o do item que estava respondendo, e continuaria errando.
   */
  const igual = (mesViz: string, v: number): RichText => [
    txt(
      `Com ${mes} igual a ${mesViz} (${n0(v)} ${u}), ${ctx.sujeito} não ${comoEm(ctx.sujeito, 'mudar.futpret')} de um mês para o outro. `,
    ),
    ...(p.modo === 'progressao'
      ? [
          txt(
            `O enunciado diz que ${ctx.sujeito} ${verboDoSentido} SEMPRE a mesma quantia: de ${mesAntes} para ${mes} a variação tem de ser ${n0(Math.abs(p.razao))} ${u}, e não zero.`,
          ),
        ]
      : [
          txt(
            `O enunciado diz que ${ctx.sujeito} ${verboDoSentido} todos os meses: ${mes} precisa ser ${sobe ? 'maior' : 'menor'} que ${mesAntes}`,
          ),
          txt(depois !== null ? ` e ${sobe ? 'menor' : 'maior'} que ${mesDepois}.` : '.'),
        ]),
  ];

  erros.push({ value: antes, error: GRAF_IGUAL_VIZINHO, explanation: igual(mesAntes, antes) });
  if (depois !== null)
    erros.push({
      value: depois,
      error: GRAF_IGUAL_VIZINHO,
      explanation: igual(mesDepois as string, depois),
    });

  // Do lado "errado" do mês anterior: a sequência muda de sentido já na chegada.
  const quebraAntes: RichText = [
    txt(
      `Com esse valor, ${mes} fica ${sobe ? 'abaixo' : 'acima'} de ${mesAntes} (${n0(antes)} ${u}): `,
    ),
    txt(
      `de ${mesAntes} para ${mes}, ${ctx.sujeito} ${sentidoDo(ctx, !sobe)} — o contrário do que diz o enunciado.`,
    ),
  ];
  const quebraDepois: RichText =
    depois === null
      ? []
      : [
          txt(
            `Com esse valor, ${mes} fica ${sobe ? 'acima' : 'abaixo'} de ${mesDepois} (${n0(depois)} ${u}): `,
          ),
          txt(
            `de ${mes} para ${mesDepois}, ${ctx.sujeito} ${sentidoDo(ctx, !sobe)} — o contrário do que diz o enunciado.`,
          ),
        ];

  if (sobe) {
    faixasDeErro.push({
      lo: 0,
      hi: antes - 1,
      error: GRAF_QUEBRA_TENDENCIA,
      explanation: quebraAntes,
    });
    if (depois !== null)
      faixasDeErro.push({
        lo: depois + 1,
        hi: p.yTop,
        error: GRAF_QUEBRA_TENDENCIA,
        explanation: quebraDepois,
      });
  } else {
    faixasDeErro.push({
      lo: antes + 1,
      hi: p.yTop,
      error: GRAF_QUEBRA_TENDENCIA,
      explanation: quebraAntes,
    });
    if (depois !== null)
      faixasDeErro.push({
        lo: 0,
        hi: depois - 1,
        error: GRAF_QUEBRA_TENDENCIA,
        explanation: quebraDepois,
      });
  }

  if (p.modo === 'tendencia') {
    const d = depois as number;
    const lo = Math.min(antes, d) + 1;
    const hi = Math.max(antes, d) - 1;
    return { chave: Math.round((antes + d) / 2), faixa: { lo, hi }, erros, faixasDeErro };
  }

  // --- progressão aritmética ------------------------------------------------
  const r = p.razao;
  const chave = antes + r;
  const ar = Math.abs(r);
  const candidatos: { value: number; error: ErrorCode; explanation: RichText }[] = [
    {
      value: antes + 2 * r,
      error: GRAF_PA_DIFERENCA_DOBRADA,
      explanation: [
        txt(
          `Você ${sobe ? 'somou' : 'subtraiu'} ${n0(2 * ar)} a ${mesAntes}: ${n0(antes)} ${sobe ? '+' : '−'} ${n0(2 * ar)} = ${n0(antes + 2 * r)}. `,
        ),
        txt(
          `Mas ${n0(2 * ar)} é a variação de DOIS meses. De um mês para o seguinte, ${ctx.sujeito} ${verboDoSentido} ${n0(ar)} ${u}.`,
        ),
      ],
    },
  ];
  const primeiro = p.valores[0] as number;
  if (sobe && primeiro !== ar && p.alvo - 1 !== 0) {
    candidatos.push({
      value: antes + primeiro,
      error: GRAF_PA_DIFERENCA_ERRADA,
      explanation: [
        txt(
          `Você somou a ${mesAntes} o valor de janeiro, ${n0(primeiro)}: ${n0(antes)} + ${n0(primeiro)} = ${n0(antes + primeiro)}. `,
        ),
        txt(
          `O aumento mensal não é o valor de um mês, e sim a DIFERENÇA entre dois meses vizinhos: ${n0(ar)} ${u}.`,
        ),
      ],
    });
  }
  const conhecidos = p.valores.filter((_v, i) => i !== p.alvo);
  const media = Math.round(conhecidos.reduce((s, v) => s + v, 0) / conhecidos.length);
  candidatos.push({
    value: media,
    error: GRAF_PA_MEDIA,
    explanation: [
      txt(`${n0(media)} é, arredondada, a média dos outros cinco meses. `),
      txt(
        `Mas o padrão do gráfico não é "ficar na média": de um mês para o outro, ${ctx.sujeito} ${verboDoSentido} sempre ${n0(ar)} ${u}.`,
      ),
    ],
  });

  // Só entram os diagnósticos que o seletor alcança e que não se confundem entre si.
  const distintos = (v: number): boolean =>
    v >= 0 &&
    v <= p.yTop &&
    Math.abs(v - chave) > 1 &&
    erros.every((e) => Math.abs(e.value - v) > 1);
  for (const c of candidatos) if (distintos(c.value)) erros.push(c);

  // Entre os vizinhos, mas fora da razão: a sequência segue o sentido certo, com saltos desiguais.
  const lo = depois === null ? (sobe ? antes + 1 : 0) : Math.min(antes, depois) + 1;
  const hi = depois === null ? (sobe ? p.yTop : antes - 1) : Math.max(antes, depois) - 1;
  faixasDeErro.push({
    lo,
    hi,
    error: GRAF_PA_DIFERENCA_ERRADA,
    explanation: [
      txt(
        `Com esse valor, ${ctx.sujeito} segue ${sobe ? 'crescendo' : 'diminuindo'}, mas não na mesma quantia todo mês. `,
      ),
      txt(
        `Nos meses conhecidos, a variação é sempre de ${n0(ar)} ${u}; ${mes} tem de ser ${n0(antes)} ${sobe ? '+' : '−'} ${n0(ar)} = ${n0(chave)}.`,
      ),
    ],
  });

  return { chave, faixa: null, erros, faixasDeErro };
}

// ---------------------------------------------------------------------------
// Template
// ---------------------------------------------------------------------------

function construirTemplate(modo: Modo): ItemTemplate<Params, number> {
  const tendencia = modo === 'tendencia';
  return {
    id: templateId(tendencia ? 'h3-30-K01-grafico-tendencia' : 'h1-12-K01-grafico-progressao'),
    // v2: a coluna a definir é só um "?", sem retângulo claro.
    version: 2,
    taskModel: taskModelCode(tendencia ? 'H3.30.K01' : 'H1.12.K01'),
    skills: tendencia
      ? [skillCode('H3.30'), skillCode('H1.9')]
      : [skillCode('H1.12'), skillCode('H3.30')],
    axis: 'II',
    level: 'N3',
    title: tendencia
      ? 'Coluna que mantém a tendência de um gráfico'
      : 'Coluna de um gráfico com variação constante',
    // Resposta produzida, sem alternativas: não há acerto ao acaso a modelar.
    responseModel: '2PL',

    difficultySources: tendencia ? ['select-information'] : ['steps.2', 'select-information'],

    params: paramsPara(modo),
    solve: (p) => resposta(p).chave,
    buggy: [],

    variantDifficulty(p) {
      const fontes: DifficultySourceCode[] = [];
      if (p.sentido === 'decresce') fontes.push('reading-load.high');
      // Último mês: não há vizinho depois; é preciso continuar a sequência.
      if (p.modo === 'progressao' && p.alvo === 5) fontes.push('inverse-problem');
      return fontes;
    },

    build(p) {
      const ctx = CONTEXTOS[p.contexto] as Contexto;
      const res = resposta(p);
      const mes = MESES_NOME[p.alvo] as string;
      const verboDoSentido = sentidoDo(ctx, p.sentido === 'cresce');

      const conhecidos = p.valores
        .map((v, i) => (i === p.alvo ? null : `${MESES_NOME[i]}, ${n0(v)}`))
        .filter((x): x is string => x !== null)
        .join('; ');
      const desc =
        // O título do eixo entre aspas, como o do gráfico: entre parênteses ele saía
        // "(Visitantes (mil))", em todos os contextos, que já trazem a unidade assim.
        `Gráfico de colunas "${ctx.titulo}", de janeiro a junho, com o eixo vertical "${ctx.eixo}" de 0 a ${n0(p.yTop)}, ` +
        `numerado de ${Y_STEP} em ${Y_STEP} e com linhas de grade de ${GRID} em ${GRID}. ` +
        `Valores: ${conhecidos}. A coluna de ${mes} começa vazia; um seletor define a altura dela, de 0 a ${n0(p.yTop)}.`;

      const informacao: RichText = tendencia
        ? [
            txt(
              `Sabe-se que ${ctx.sujeito} ${verboDoSentido} de um mês para o outro durante todo o semestre, mas a coluna de ${mes} ainda não foi desenhada.`,
            ),
          ]
        : [
            txt(`Sabe-se que ${ctx.sujeito} ${verboDoSentido} `),
            strong('sempre a mesma quantidade'),
            txt(` de um mês para o outro, mas a coluna de ${mes} ainda não foi desenhada.`),
          ];

      const interaction: NumericEntryInteraction = {
        kind: 'numeric-entry',
        unit: ctx.unidade,
        decimals: 0,
        keyValue: res.chave,
        absTol: 0.5,
        errorValues: res.erros,
        errorIntervals: res.faixasDeErro,
        ...(res.faixa ? { accept: res.faixa } : {}),
        viaControl: { stimulusId: 'grafico', controlId: 'valor' },
      };

      const r = Math.abs(p.razao);
      const antes = p.valores[p.alvo - 1] as number;
      const depois = p.valores[p.alvo + 1];

      return {
        stem: [txt(ctx.abertura), br, ...informacao],
        comando: tendencia
          ? [
              txt('Use o seletor para dar à coluna de '),
              strong(mes),
              txt(' um valor compatível com essa informação. '),
              // Diferente do item irmão, aqui há uma FAIXA de respostas certas. Sem
              // dizer isso, o aluno procura "o" valor e trava entre dois igualmente bons.
              txt('Há mais de um valor possível: basta escolher um.'),
            ]
          : [
              txt('Use o seletor para dar à coluna de '),
              strong(mes),
              txt(' o valor que mantém esse padrão.'),
            ],
        stimuli: [
          {
            kind: 'manipulable',
            motion: 'interactive',
            audio: { need: 'none' },
            id: 'grafico',
            alt: desc,
            controls: [
              {
                id: 'valor',
                label: [txt(`Coluna de ${mes}`)],
                min: 0,
                max: p.yTop,
                step: 1,
                initial: 0,
                format: (v) => (v === 0 ? '—' : `${n0(v)} ${ctx.unidade}`),
              },
            ],
            frame: (valores) => {
              const v = Math.round(valores['valor'] ?? 0);
              return barras({
                categorias: MESES,
                valores: p.valores.map((x, i) => (i === p.alvo ? (v > 0 ? v : null) : x)),
                destaque: p.alvo,
                yTop: p.yTop,
                yStep: Y_STEP,
                gridStep: GRID,
                xLabel: 'Mês',
                yLabel: ctx.eixo,
                title: ctx.titulo,
                desc: v > 0 ? `${desc} Valor escolhido para ${mes}: ${n0(v)}.` : desc,
              });
            },
          },
        ],
        interaction,
        dicas: tendencia
          ? [
              [
                txt(
                  `O enunciado diz que ${ctx.sujeito} ${verboDoSentido} todos os meses. Compare ${mes} com o mês anterior e com o seguinte.`,
                ),
              ],
              [
                txt(
                  'Qualquer valor entre os dois meses vizinhos serve — mas não um valor igual a um deles.',
                ),
              ],
            ]
          : [
              [txt('Calcule a diferença entre dois meses vizinhos que o gráfico mostra.')],
              [
                txt(
                  `Essa diferença se repete de um mês para o outro. Aplique-a ao mês anterior a ${mes}.`,
                ),
              ],
            ],
        rationale: tendencia
          ? [
              strong('Resolução. '),
              txt(
                `Para ${ctx.sujeito} ter ${p.sentido === 'cresce' ? 'crescido' : 'diminuído'} todos os meses, ` +
                  `${mes} precisa ficar ${p.sentido === 'cresce' ? 'acima' : 'abaixo'} de ${MESES_NOME[p.alvo - 1]} (${n0(antes)}) e ` +
                  `${p.sentido === 'cresce' ? 'abaixo' : 'acima'} de ${MESES_NOME[p.alvo + 1]} (${n0(depois as number)}).`,
              ),
              br,
              txt(
                `Qualquer valor de ${n0((res.faixa as { lo: number }).lo)} a ${n0((res.faixa as { hi: number }).hi)} ${ctx.unidade} está correto — ` +
                  `por exemplo, ${n0(res.chave)}. Valores iguais aos vizinhos não servem: não haveria ${p.sentido === 'cresce' ? 'crescimento' : 'queda'} naquele mês.`,
              ),
            ]
          : [
              strong('Resolução. '),
              txt(
                `Nos meses conhecidos, a diferença entre dois meses vizinhos é sempre ${n0(r)} ${ctx.unidade}: ` +
                  `${ctx.sujeito} ${verboDoSentido} ${n0(r)} por mês.`,
              ),
              br,
              txt(
                `Logo, ${mes} = ${MESES_NOME[p.alvo - 1]} ${p.razao > 0 ? '+' : '−'} ${n0(r)} = ${n0(antes)} ${p.razao > 0 ? '+' : '−'} ${n0(r)} = ${n0(res.chave)} ${ctx.unidade}.`,
              ),
              ...(depois !== undefined
                ? [
                    br,
                    txt(
                      `Conferindo: de ${mes} para ${MESES_NOME[p.alvo + 1]}, ${n0(res.chave)} ${p.razao > 0 ? '+' : '−'} ${n0(r)} = ${n0(depois)}.`,
                    ),
                  ]
                : []),
            ],
        declared: tendencia ? 'facil' : 'media',
      };
    },

    validate(_b, p) {
      const res = resposta(p);
      if (res.chave <= 0 || res.chave > p.yTop) return 'a resposta sai do alcance do seletor';
      // Nenhum diagnóstico pontual pode cair dentro da faixa de respostas certas.
      if (
        res.faixa &&
        res.erros.some((e) => e.value >= res.faixa!.lo && e.value <= res.faixa!.hi)
      ) {
        return 'diagnóstico dentro da faixa correta';
      }
      return null;
    },
  };
}

export const graficoTendencia = construirTemplate('tendencia');
export const graficoProgressao = construirTemplate('progressao');
